import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { AuthLayout, Button, Field, Notice } from "../components";
import { useRouter, Link } from "../router";
import { useSession } from "../session";

type Step = { kind: "credentials" } | { kind: "mfa"; challenge: string } | { kind: "enroll"; enrollment: string; secret?: string; uri?: string };

export function Login() {
  const { navigate } = useRouter();
  const { signedIn } = useSession();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noOrg, setNoOrg] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (step.kind !== "credentials") codeRef.current?.focus(); }, [step.kind]);

  async function handle(result: any) {
    if (result.status === "ok") {
      const where = await signedIn(result.accessToken, result.memberships);
      if (where === "app") navigate("/app", true);
      else if (where === "choose") navigate("/select-organisation", true);
      else if (where === "platform") navigate("/platform", true);
      else setNoOrg(true);
    } else if (result.status === "mfa_required") { setStep({ kind: "mfa", challenge: result.challenge }); setCode(""); }
    else if (result.status === "mfa_enrollment_required") {
      const e = await api.post("/api/v1/auth/mfa/enroll/start", { enrollment: result.enrollment });
      setStep({ kind: "enroll", enrollment: result.enrollment, secret: e.secret, uri: e.otpauthUri }); setCode("");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try {
      if (step.kind === "credentials") await handle(await api.post("/api/v1/auth/login", { identifier, password }));
      else if (step.kind === "mfa") await handle(await api.post("/api/v1/auth/mfa", { challenge: step.challenge, code }));
      else await handle(await api.post("/api/v1/auth/mfa/enroll/complete", { enrollment: step.enrollment, code }));
    } catch (err) {
      const ex = err as ApiError;
      setError(step.kind !== "credentials" && ex.code === "invalid_credentials" ? "That code didn't work. Enter the current 6-digit code from your authenticator app." : ex.message);
      if (step.kind !== "credentials") setCode("");
    } finally { setBusy(false); }
  }

  if (noOrg) return (
    <AuthLayout title="No organisation yet" subtitle="Your account isn't linked to a clinic or hospital. Ask your administrator to invite you, or set up your own.">
      <Link to="/signup" className="btn btn-primary">Set up a clinic or hospital</Link>
    </AuthLayout>
  );

  const codeField = (
    <Field label="6-digit code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} ref={codeRef as any} />
  );

  return (
    <AuthLayout
      title={step.kind === "credentials" ? "Sign in" : step.kind === "mfa" ? "Enter your verification code" : "Set up two-step verification"}
      subtitle={step.kind === "mfa" ? "Open your authenticator app and enter the code shown for HealthOS."
        : step.kind === "enroll" ? "Your role requires a second step at sign-in. Add HealthOS to an authenticator app such as Google Authenticator or Microsoft Authenticator." : undefined}>
      <form onSubmit={submit} noValidate={false}>
        {error && <Notice tone="error">{error}</Notice>}
        {step.kind === "credentials" && <>
          <Field label="Email or mobile number" autoComplete="username" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoFocus />
          <Field label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </>}
        {step.kind === "enroll" && step.secret && (
          <div className="secret-box">
            <p>Enter this setup key in your app, or <a href={step.uri}>open it in your authenticator app</a> on this device.</p>
            <code aria-label="Setup key">{step.secret.match(/.{1,4}/g)!.join(" ")}</code>
          </div>
        )}
        {step.kind !== "credentials" && codeField}
        <Button type="submit" busy={busy}>{step.kind === "credentials" ? "Sign in" : "Verify"}</Button>
      </form>
      {step.kind === "credentials" && <p className="aside">New to HealthOS? <Link to="/signup">Set up your clinic or hospital</Link></p>}
      {step.kind !== "credentials" && <p className="aside"><button className="link-button" onClick={() => { setStep({ kind: "credentials" }); setError(null); }}>Use a different account</button></p>}
    </AuthLayout>
  );
}
